import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCampaigns, useBatchCampaignState, type Campaign, type CampaignFilters } from '@/api/campaigns'
import { reconcileRequiredCount } from '@/api/client'
import { StateBadge, AdTypeBadge } from '@/components/StatusBadge'
import DataTable, { type DataTableColumn } from '@/components/DataTable'
import Pagination from '@/components/Pagination'
import FilterBar from '@/components/FilterBar'
import DateRangePicker from '@/components/DateRangePicker'
import { useListQuery } from '@/lib/useListQuery'
import { Pause, Play, Archive, AlertTriangle } from 'lucide-react'

function defaultDateRange(): { from: string; to: string } {
  const to = new Date()
  const from = new Date()
  from.setDate(from.getDate() - 30)
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) }
}

function fmtEur(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function fmtPct(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return `${n.toFixed(1)}%`
}
function fmtRoas(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return n.toFixed(2)
}
function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return n.toLocaleString('de-DE')
}

export default function CampaignList() {
  const navigate = useNavigate()
  const defaults = defaultDateRange()
  const {
    state,
    setPage,
    setPageSize,
    toggleSort,
    setSearch,
    setDateRange,
    setFilter,
  } = useListQuery({
    pageSize: 50,
    sortBy: 'spend',
    sortDir: 'desc',
    filters: { state: 'enabled' },
  })

  const [searchInput, setSearchInput] = useState(state.search ?? '')
  const [selected, setSelected] = useState<Set<number>>(new Set())
  // M9 (Scheibe E): Zahl der Eintraege des letzten Batch-Laufs, deren Amazon-Zustand
  // ungeklaert blieb (HTTP 502 / RECONCILE_REQUIRED). 0 = kein Hinweis.
  const [reconcileCount, setReconcileCount] = useState(0)

  const filters: CampaignFilters = useMemo(() => ({
    limit: state.pageSize,
    offset: state.page * state.pageSize,
    sortBy: state.sortBy,
    sortDir: state.sortDir,
    search: state.search,
    adType: state.filters['adType'],
    state: state.filters['state'],
    dateFrom: state.dateFrom ?? defaults.from,
    dateTo: state.dateTo ?? defaults.to,
  }), [state, defaults.from, defaults.to])

  const { data, isLoading, error } = useCampaigns(filters)
  const batchState = useBatchCampaignState()
  const campaigns = data?.campaigns ?? []
  const total = data?.total ?? 0

  function onToggleAll() {
    if (selected.size === campaigns.length) setSelected(new Set())
    else setSelected(new Set(campaigns.map((c) => c.id)))
  }

  function onToggleOne(id: string | number) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id as number)) next.delete(id as number)
      else next.add(id as number)
      return next
    })
  }

  async function handleBatchState(st: string) {
    if (selected.size === 0) return
    setReconcileCount(0)
    try {
      await batchState.mutateAsync({ ids: [...selected], state: st })
    } catch (err) {
      // Auswahl BEWUSST erhalten — nach einem Fehlschlag muesste der Operator sie
      // sonst neu zusammensuchen, gerade dann, wenn Teile des Batches offen sind.
      // `reconcileRequiredCount` liefert nur fuer den Batch-502 (`ackOrReconcile`)
      // einen Wert > 0; ein `nack(502, …)` ohne `result` und jeder andere Fehler
      // ergeben 0 und bleiben beim gewohnten globalen Toast.
      setReconcileCount(reconcileRequiredCount(err))
      return
    }
    setSelected(new Set())
  }

  const columns: DataTableColumn<Campaign>[] = [
    { key: 'name', label: 'Name', sortable: true, render: (c) => <span className="font-medium truncate block max-w-[280px]" title={c.name}>{c.name}</span> },
    { key: 'adType', label: 'Typ', render: (c) => <AdTypeBadge adType={c.adType} /> },
    { key: 'state', label: 'Status', render: (c) => <StateBadge state={c.state} /> },
    { key: 'budget', label: 'Budget', align: 'right', sortable: true, render: (c) => c.budget ? `${parseFloat(c.budget).toFixed(2)} €` : '—' },
    { key: 'spend', label: 'Spend', align: 'right', sortable: true, render: (c) => fmtEur(c.spend) },
    { key: 'sales', label: 'Sales', align: 'right', sortable: true, render: (c) => fmtEur(c.sales) },
    { key: 'orders', label: 'Orders', align: 'right', sortable: true, render: (c) => fmtInt(c.orders) },
    { key: 'acos', label: 'ACoS', align: 'right', sortable: true, render: (c) => fmtPct(c.acos) },
    { key: 'roas', label: 'ROAS', align: 'right', sortable: true, render: (c) => fmtRoas(c.roas) },
    { key: 'impressions', label: 'Impr.', align: 'right', sortable: true, render: (c) => fmtInt(c.impressions) },
    { key: 'clicks', label: 'Clicks', align: 'right', sortable: true, render: (c) => fmtInt(c.clicks) },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Campaigns</h1>
        <span className="text-sm text-[var(--muted-foreground)]">{total.toLocaleString('de-DE')} gesamt</span>
      </div>

      {/* M9 (Scheibe E): So erreicht ein ungeklaerter Amazon-Zustand aus einem
          Batch-Write dieser Liste den Operator. Statt Toast — der verschwindet nach
          Sekunden, waehrend die betroffene Auswahl stehen bleibt. Bewusst KEIN Link
          auf den Prüfen-Tab: dessen Auswahl ist reiner Komponenten-State ohne URL. */}
      {reconcileCount > 0 && (
        <div role="alert" className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-800">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>{`${reconcileCount} Einträge prüfbedürftig — Amazon-Zustand ungeklärt. Details: Inbox → Prüfen.`}</span>
        </div>
      )}

      <FilterBar
        search={searchInput}
        onSearch={(v) => { setSearchInput(v); setSearch(v) }}
        searchPlaceholder="Kampagne suchen..."
        trailing={
          <DateRangePicker
            value={{ from: state.dateFrom ?? defaults.from, to: state.dateTo ?? defaults.to }}
            onChange={(r) => setDateRange(r.from, r.to)}
          />
        }
      >
        <select
          value={state.filters['adType'] ?? ''}
          onChange={(e) => setFilter('adType', e.target.value)}
          className="h-9 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
        >
          <option value="">Alle Typen</option>
          <option value="sp">SP</option>
          <option value="sb">SB</option>
          <option value="sd">SD</option>
        </select>
        <select
          value={state.filters['state'] ?? ''}
          onChange={(e) => setFilter('state', e.target.value)}
          className="h-9 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
        >
          <option value="">Alle Status</option>
          <option value="enabled">Enabled</option>
          <option value="paused">Paused</option>
          <option value="archived">Archived</option>
        </select>
      </FilterBar>

      {selected.size > 0 && (
        <div className="flex items-center gap-2 rounded-md bg-[var(--accent)] px-3 py-2 text-sm">
          <span className="font-medium">{selected.size} ausgewaehlt</span>
          <button onClick={() => handleBatchState('enabled')} className="ml-4 flex items-center gap-1 rounded px-2 py-1 text-emerald-700 hover:bg-emerald-100" disabled={batchState.isPending}>
            <Play className="h-3.5 w-3.5" /> Enable
          </button>
          <button onClick={() => handleBatchState('paused')} className="flex items-center gap-1 rounded px-2 py-1 text-amber-700 hover:bg-amber-100" disabled={batchState.isPending}>
            <Pause className="h-3.5 w-3.5" /> Pause
          </button>
          <button onClick={() => handleBatchState('archived')} className="flex items-center gap-1 rounded px-2 py-1 text-zinc-500 hover:bg-zinc-100" disabled={batchState.isPending}>
            <Archive className="h-3.5 w-3.5" /> Archivieren
          </button>
        </div>
      )}

      {error ? (
        <div className="py-12 text-center text-sm text-red-500">Fehler: {(error as Error).message}</div>
      ) : (
        <DataTable<Campaign>
          columns={columns}
          rows={campaigns}
          getRowId={(c) => c.id}
          sortBy={state.sortBy}
          sortDir={state.sortDir}
          onSort={toggleSort}
          onRowClick={(c) => navigate(`/campaigns/${c.id}`)}
          selection={{ selected: selected as Set<string | number>, onToggle: onToggleOne, onToggleAll }}
          loading={isLoading}
          emptyMessage="Keine Kampagnen gefunden"
        />
      )}

      <Pagination
        page={state.page}
        pageSize={state.pageSize}
        totalItems={total}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  )
}
